import React, { useState, useEffect, useCallback ,useRef} from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './Login';
import CompanyYearSelection from './CompanyYearSelection';
import Dashboard from './Dashboard';
import DashboardHome from './DashboardHome';
import Countries from './Countries';
import States from './States';
import Cities from './Cities';
import Customers from './Customers';
import Employees from './Employees';
import Departments from './Departments';
import Designations from './Designations';
import Vehicles from './Vehicles';
import AccountGroups from './AcGroups';
import AccountHeads from './AcHeads';
import DRSTransactionEntry from './DRSTransactionEntry';
import DRSList from './DRSList';   
import '../styles/App.css';
import BankVoucher from './BankVoucher';
import CashVoucher from './CashVoucher';
import JournalEntry from './JournalEntry';
import ConsignmentLT from './ConsignmentLT';
import ConsignmentD from './ConsignmentD';
import ConsignmentI from './ConsignmentI';
import { verifySession } from '../services/api';
import JobList from './JobList';
import Currencies from './Currencies';
import MenuItems from './MenuItems';
import Users from './Users';
import UserTypes from './UserTypes';
import UserTypeMenuAccessMatrixerTypes from './UserTypeMenuAccessMatrix';
import UserCompanyAccess from './UserCompanyAccess';
import UserTypeMenuAccess from './UserTypeMenuAccess';
import GeneralSetup from './GeneralSetup';
import ExchangeRates from './ExchangeRates';
import Suppliers from './Suppliers';
import SupplierTypes from './SupplierTypes'; 
import PODUpdateForm from './PODUpdateForm'; 
import Drivers from './Drivers'; 
import ConsignmentPaymentWithoutCurrency from './ConsignmentPaymentWithoutCurrency';
import PaymentModes from './PaymentModes';
import CustomerInvoice from './CustomerInvoice';
import SupplierInvoice from './SupplierInvoice';
import CustomerReceipts from './CustomerReceipts';
import SupplierPayments from './SupplierPayments';
import Outscan from './Outscan';
import Manifest from './ManifestReport';  
import TrialBalance from './TrialBalance';
import LedgerReport from './LedgerReport';
import DayBook from './DayBook';
import ConsignmentTracking from './ConsignmentTracking';
import VehicleAssignments from './VehicleAssignments';
import CourierStatus from './CourierStatus';
import EmployeeTypes from './EmployeeTypes';
import EmploymentStatuses from './EmploymentStatuses'; 
import ConsignmentStatusEntry from './ConsignmentStatusEntry';
import JobType from './JobType'; 
import ShippingAgent from './ShippingAgent';
import JobMode from './JobMode';
import RevenueType from './RevenueType'; 
import Port  from './Port';
import ContainerType from './ContainerType';
import Vessel from './Vessel';
import Carrier from './Carrier';
import Airline from './Airline';
import Transporter from './Transporter';
import ConsignmentReports from './ConsignmentReports';
import CustomerReports from './CustomerReports';
import SupplierReports from './SupplierReports';
import FinancialReports from './FinancialReports';
import JobReports from './JobReports';
import IdleLogoutManager from './IdleLogoutManager';
import Subscriptions from './Subscriptions';  
import DeliveryStatus from './DeliveryStatus';
import UnitMaster from './UnitMaster';
import DocTypeMaster from './DocTypeMaster';
import JobReceipts from './JobReceipts'; 
import JobSupplierPayments from './JobSupplierPayments';
import ControlHeadAssignForm from './ControlHeadAssignForm';
import TaxMaster from './TaxMaster';
import DatabaseBackupForm from './DatabaseBackupForm';
import ActiveSessions from './ActiveSessions';
import SessionHistory from './SessionHistory'

function App() { 
  
  const sessionValidated = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [requiresSelection, setRequiresSelection] = useState(false);
  const [userData, setUserData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [isValidating, setIsValidating] = useState(true);

  // Log state changes for debugging
  useEffect(() => {
    console.log('📊 App State Updated:', {
      isAuthenticated,
      requiresSelection,
      hasUserData: !!userData,
      hasSessionData: !!sessionData,
      timestamp: new Date().toISOString()
    });
  }, [isAuthenticated, requiresSelection, userData, sessionData]);

  const validateStoredSession = useCallback(async () => {
    console.log('🔍 Validating stored session...');
    const savedSession = localStorage.getItem('ltms_session');
    
    if (!savedSession) {
      console.log('ℹ️ No saved session found');
      setIsValidating(false);
      return;
    }

    try {
      const session = JSON.parse(savedSession);
      console.log('📦 Found saved session:', session);
      
      const response = await verifySession();
      
      if (response.success) {
        console.log('✅ Session validated successfully');
        setSessionData(session);
        setIsAuthenticated(true);
        setRequiresSelection(false);
      } else {
        console.log('❌ Session validation failed:', response.message);
        clearSession();
      }
    } catch (error) {
      console.error('❌ Session validation error:', error);
      clearSession();
    } finally {
      setIsValidating(false);
    }
  }, []);

  useEffect(() => {
  if (sessionValidated.current) return;
      sessionValidated.current = true;
      validateStoredSession();
    }, [validateStoredSession]);

  const clearSession = () => {
    console.log('🗑️ Clearing session data');
    localStorage.removeItem('ltms_session');
    localStorage.removeItem('sessionData');
    setUserData(null);
    setSessionData(null);
    setIsAuthenticated(false);
    setRequiresSelection(false);
  };

  const handleLoginSuccess = (data) => {
    console.log('✅ Login success:', data); 
    setUserData(data);
    
    if (data.requires_selection) {
      console.log('➡️ User needs to select company/year');
      setRequiresSelection(true);
      setIsAuthenticated(false);
    } else {
      console.log('✅ Session established, navigating to dashboard');
      setSessionData(data);
      setIsAuthenticated(true);
      setRequiresSelection(false);
      localStorage.setItem('ltms_session', JSON.stringify(data));
    }
  };

  const handleSelectionComplete = (data) => {
    console.log('✅ Selection complete:', data);
    console.log('📦 Setting session data and authentication');
    setSessionData(data);
    setRequiresSelection(false);
    setIsAuthenticated(true);
    localStorage.setItem('ltms_session', JSON.stringify(data));
    console.log('✅ Navigation to dashboard should happen now');
  };

  const handleLogout = () => {
    console.log('👋 User logging out');
    clearSession();
  };

  const ComingSoon = ({ title }) => (
    <div className="page-placeholder" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '400px',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚧</div>
      <h2>{title}</h2>
      <p style={{ color: '#666', marginTop: '10px' }}>This page is under construction</p>
    </div>
  );

  if (isValidating) {
    return (
      <div className="App" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <div>Validating session...</div>
        </div>
      </div>
    );
  }

  console.log('🎨 Rendering App with routes');

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          {/* Login Route */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : requiresSelection ? (
                <Navigate to="/selection" replace />
              ) : (
                <Login onLoginSuccess={handleLoginSuccess} />
              )
            } 
          />

          {/* Company/Year Selection Route */}
          <Route 
            path="/selection" 
            element={
              !requiresSelection ? (
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              ) : (
                <CompanyYearSelection 
                  userData={userData} 
                  onSelectionComplete={handleSelectionComplete}
                  onLogout={handleLogout}
                />
              )
            } 
          />

          {/* Dashboard Routes */}
          <Route 
            path="/dashboard"
            element={
              !isAuthenticated ? (
                <Navigate to={requiresSelection ? "/selection" : "/login"} replace />
              ) : (
                <>
                  <Dashboard sessionData={sessionData} onLogout={handleLogout} />
                  <IdleLogoutManager onLogout={handleLogout} enabled={isAuthenticated} timeoutMins={15} warningMins={2} />
                </>
              )
            }
          >
            {/* Dashboard Home - Index Route */}
            <Route index element={<DashboardHome sessionData={sessionData} />} />
            
            {/* Operations Routes */}
            <Route path="consignments-lt" element={<ConsignmentLT />} />
            <Route path="consignments-d" element={<ConsignmentD />} />
            <Route path="consignments-i" element={<ConsignmentI />} />
            <Route path="consignment-payment" element={<ConsignmentPaymentWithoutCurrency />} />
            <Route path="consignment-status-entry" element={<ConsignmentStatusEntry />} />
            <Route path="containers" element={<ComingSoon title="Containers Management" />} />
            <Route path="drs-entry" element={<DRSTransactionEntry />} />
            <Route path="drs-list" element={<DRSList />} />
            <Route path="jobs" element={<JobList />} />
            <Route path="pod-update" element={<PODUpdateForm />} />
            <Route path="customer-invoice" element={<CustomerInvoice />} />
            <Route path="supplier-invoice" element={<SupplierInvoice />} />
            <Route path="customer-receipts" element={<CustomerReceipts />} />
            <Route path="supplier-payments" element={<SupplierPayments />} />
            <Route path="outscan" element={<Outscan />} />
            <Route path="manifest" element={<Manifest />} />

            {/* Masters Routes */}
            <Route path="countries" element={<Countries />} />
            <Route path="states" element={<States />} />
            <Route path="cities" element={<Cities />} />
            <Route path="customers" element={<Customers />} />
            <Route path="employees" element={<Employees />} />
            <Route path="departments" element={<Departments />} />
            <Route path="designations" element={<Designations />} />
            <Route path="vehicles" element={<Vehicles />} />
            <Route path="currencies" element={<Currencies />} />
            <Route path="exchange-rates" element={<ExchangeRates />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="supplier-types" element={<SupplierTypes />} /> 
            <Route path="drivers" element={<Drivers />} />
            <Route path="payment-modes" element={<PaymentModes />} />
            <Route path="vehicle-assignments" element={<VehicleAssignments />} />
            <Route path="courier-status" element={<CourierStatus />} />
            <Route path="employee-types" element={<EmployeeTypes />} />
            <Route path="employment-statuses" element={<EmploymentStatuses />} />
            <Route path="job-types" element={<JobType />} />
            <Route path="shipping-agents" element={<ShippingAgent />} />
            <Route path="job-modes" element={<JobMode />} />
            <Route path="revenue-types" element={<RevenueType />} />
            <Route path="ports" element={<Port />} />
            <Route path="container-types" element={<ContainerType />} />
            <Route path="vessels" element={<Vessel />} />
            <Route path="carriers" element={<Carrier />} />
            <Route path="airlines" element={<Airline />} />
            <Route path="transporters" element={<Transporter />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="delivery-status" element={<DeliveryStatus />} />
            <Route path="units" element={<UnitMaster />} />
            <Route path="document-types" element={<DocTypeMaster />} />
            <Route path="job-receipts" element={<JobReceipts />} />
            <Route path="job-supplier-payments" element={<JobSupplierPayments />} />
            
            {/* Accounting Routes */}
            <Route path="bank-voucher" element={<BankVoucher />} />
            <Route path="cash-voucher" element={<CashVoucher />} />
            <Route path="journal-entry" element={<JournalEntry />} />
            <Route path="acgroups" element={<AccountGroups />} />
            <Route path="acheads" element={<AccountHeads />} />

            {/* Reports Routes */}
            <Route path="consignment-reports" element={<ConsignmentReports />} />
            <Route path="customer-reports" element={<CustomerReports />} />
            <Route path="supplier-reports" element={<SupplierReports />} />
            <Route path="financial-reports" element={<FinancialReports />} />
            <Route path="job-reports" element={<JobReports />} />
            <Route path="trial-balance" element={<TrialBalance />} />
            <Route path="ledger" element={<LedgerReport />} />
            <Route path="day-book" element={<DayBook />} />
            <Route path="reports/drs" element={<ComingSoon title="DRS Reports" />} />
            <Route path="reports/custom" element={<ComingSoon title="Custom Reports" />} />
            <Route path="tracking" element={<ConsignmentTracking />} />
            <Route path="ControlHeadAssign" element={<ControlHeadAssignForm />} /> 
            <Route path="TaxMaster" element={<TaxMaster />} /> 
            <Route path="active-sessions" element={<ActiveSessions />} />    
            <Route path="session-history" element={<SessionHistory />} />
            
            {/* Settings Routes */}
            <Route path="UserTypeMenuAccessMatrixerTypes" element={<UserTypeMenuAccessMatrixerTypes />} />
            <Route path ="DatabaseBackupForm" element={<DatabaseBackupForm />} />
            <Route path="general-setup" element={<GeneralSetup />} />
            <Route path="users" element={<Users />} />
            <Route path="user-types" element={<UserTypes />} />
            <Route path="user-company-access" element={<UserCompanyAccess />} />
            <Route path="user-type-menu-access" element={<UserTypeMenuAccess />} />
            <Route path="menu-items" element={<MenuItems />} />
            <Route path="settings/user-types" element={<ComingSoon title="User Type Management" />} />
            <Route path="settings/company" element={<ComingSoon title="Company Settings" />} />
            <Route path="settings/system" element={<ComingSoon title="System Settings" />} />
          </Route>

          {/* Root redirect */}
          <Route 
            path="/" 
            element={
              <Navigate 
                to={isAuthenticated ? "/dashboard" : requiresSelection ? "/selection" : "/login"} 
                replace 
              />
            } 
          />

          {/* Catch all */}
          <Route 
            path="*" 
            element={
              <Navigate 
                to={isAuthenticated ? "/dashboard" : requiresSelection ? "/selection" : "/login"} 
                replace 
              />
            } 
          />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;